import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./JobApply.scss";

const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8082";

// ─── Company ──────────────────────────────────────────────────────────────────
const POSTS_API = `${BASE}/api/public/recruitment/jobposts`;
const DRIVE_API = `${BASE}/api/public/jobdrive-or-walkin`;
const APPLY_API = `${BASE}/api/public/candidate/apply`;
const DRIVE_APPLY_API = `${BASE}/api/public/candidate/jobdrive-or-walkin/apply`;

// ─── Consultancy ──────────────────────────────────────────────────────────────
const CONS_POSTS_API = `${BASE}/api/public/consultancy/jobpost`;
const CONS_DRIVE_API = `${BASE}/api/public/consultancy/job-drive-or-walkin`;
const CONS_APPLY_API = `${BASE}/api/public/consultancy/job-post/apply`;
const CONS_DRIVE_APPLY_API = `${BASE}/api/public/consultancy/jobdrive-or-walkin/apply`;

const JOINING_STATUS_OPTIONS = [
  ["IMMEDIATE", "Immediate"],
  ["NOTICEPERIOD", "Notice Period"],
];

const QUALIFICATION_TYPES = [
  ["SCHOOL", "School"],
  ["PRE_UNIVERSITY", "Pre-University"],
  ["BACHELOR", "Bachelor's Degree"],
  ["MASTER", "Master's Degree"],
  ["PHD", "PhD / Doctorate"],
  ["OTHER", "Other"],
];

const QUALIFICATION_OPTIONS = {
  SCHOOL: ["10th Standard", "12th Standard"],
  PRE_UNIVERSITY: [
    "Diploma",
    "Certificate Course",
    "Pre-University Course (PUC)",
  ],
  BACHELOR: [
    "B.A. (Bachelor of Arts)",
    "B.Sc. (Bachelor of Science)",
    "B.Com (Bachelor of Commerce)",
    "B.Tech / B.E. (Engineering)",
    "BCA (Computer Applications)",
    "BBA (Business Administration)",
    "B.Arch (Architecture)",
    "B.Pharm (Pharmacy)",
    "MBBS",
    "B.Nursing",
    "LLB (Law)",
    "B.Ed (Education)",
    "BHM (Hotel Management)",
    "Other Bachelor's Degree",
  ],
  MASTER: [
    "M.A. (Master of Arts)",
    "M.Sc. (Master of Science)",
    "M.Com (Master of Commerce)",
    "M.Tech / M.E. (Engineering)",
    "MCA (Computer Applications)",
    "MBA (Business Administration)",
    "M.Arch (Architecture)",
    "M.Pharm (Pharmacy)",
    "MD / MS (Medical)",
    "LLM (Law)",
    "M.Ed (Education)",
    "Other Master's Degree",
  ],
  PHD: [
    "Ph.D. in Science",
    "Ph.D. in Engineering",
    "Ph.D. in Management",
    "Ph.D. in Arts & Humanities",
    "Ph.D. in Commerce",
    "Ph.D. in Medicine",
    "Ph.D. in Law",
    "Other Doctorate",
  ],
  OTHER: [
    "Vocational Training",
    "ITI Certificate",
    "Professional Certification",
    "Other",
  ],
};

const emptyForm = {
  candidateName: "",
  email: "",
  contactNumber: "",
  qualificationType: "",
  qualification: "",
  experience: "",
  idProof: "",
  resumeUrl: "",
  skills: "",
  expectedSalary: "",
  selectedJobPostIds: [],
  applicantJoiningStatus: "",
  expectedJoiningDate: "",
  noticePeriodDays: "",
  currentLocation: "",
  currentCompany: "",
  currentCompanyDesignation: "",
  currentSalary: "",
  preferredWorkLocation: "",
  portfolioLink: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function decodeTenantId(b64) {
  try {
    const normalized = b64
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    return atob(normalized);
  } catch {
    return null;
  }
}

function timeObjToStr(t) {
  if (!t || t.hour == null) return null;
  return `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d)
    ? iso
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

// Returns true if the given ISO date/datetime string is in the past.
function isPastDate(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  return d.getTime() < Date.now();
}

function fmtTime(val) {
  if (!val) return null;
  if (typeof val === "string" && /^\d{2}:\d{2}/.test(val))
    return val.slice(0, 5);
  if (typeof val === "object" && val.hour != null) return timeObjToStr(val);
  return null;
}

function buildSalaryLabel(salaryType, budgetType) {
  const parts = [salaryType, budgetType].filter(Boolean);
  return parts.length
    ? `Expected Salary (₹ — ${parts.join(" / ")})`
    : "Expected Salary (₹)";
}

// ─── URL helpers ──────────────────────────────────────────────────────────────
const URL_FIELDS = ["resumeUrl", "portfolioLink", "idProof"];
const FIELD_ORDER = [
  "selectedJobPostId",
  "candidateName",
  "email",
  "contactNumber",
  "resumeUrl",
  "portfolioLink",
  "idProof",
];

const normalizeUrl = (v = "") => {
  const t = String(v).trim();
  if (!t) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`;
};

const toUrl = (v) => {
  const t = String(v || "").trim();
  if (!t || /\s/.test(t)) return null;
  try {
    const u = new URL(normalizeUrl(t));
    return ["http:", "https:"].includes(u.protocol) && u.hostname.includes(".")
      ? u
      : null;
  } catch {
    return null;
  }
};
const isValidUrl = (v) => Boolean(toUrl(v));
const urlOrUndef = (v) =>
  String(v || "").trim() ? normalizeUrl(v) : undefined;

// "folder" | "file" | null — used for the friendly hint under link fields
const getDriveKind = (v) => {
  const u = toUrl(v);
  if (!u || !/(^|\.)(drive|docs)\.google\.com$/i.test(u.hostname)) return null;
  return /\/folders\//.test(u.pathname) ? "folder" : "file";
};

const validateField = (name, value) => {
  const v = String(value ?? "").trim();
  switch (name) {
    case "candidateName":
      return v ? "" : "Please enter your full name";
    case "email":
      if (!v) return "Please enter your email address";
      return /^\S+@\S+\.\S+$/.test(v)
        ? ""
        : "Enter a valid email, e.g. name@example.com";
    case "contactNumber": {
      if (!v) return "Please enter your phone number";
      const digits = v.replace(/\D/g, "");
      return /^[+\d\s\-()]+$/.test(v) &&
        digits.length >= 7 &&
        digits.length <= 15
        ? ""
        : "Enter a valid phone number";
    }
    case "resumeUrl":
    case "portfolioLink":
    case "idProof":
      return !v || isValidUrl(v)
        ? ""
        : "Enter a valid link, e.g. https://drive.google.com/…";
    default:
      return "";
  }
};

const focusFirstError = (errs) => {
  const key = FIELD_ORDER.find((k) => errs[k]);
  if (!key) return;
  requestAnimationFrame(() => {
    const el = document.getElementById(`ja-${key}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  });
};

// ─── Icons (inline, no dependency) ────────────────────────────────────────────
const ICONS = {
  building: (
    <path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 9h5a1 1 0 0 1 1 1v11M3 21h18M8 8h2M8 12h2M8 16h2" />
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.3c2.2.7 3.5 2.6 3.5 5.7" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ),
  rupee: <path d="M7 5h10M7 9h10M7 5c4 0 6 1.5 6 4s-2 4-6 4l7 6" />,
  file: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </>
  ),
  gallery: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M4 17l5-4.5 3.5 3L16 12.5l4.5 4.5" />
    </>
  ),
  idcard: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6.5 16c.5-1.6 1.6-2.3 2.5-2.3s2 .7 2.5 2.3M14 10h4M14 14h3" />
    </>
  ),
  folder: (
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  ),
  folderPlus: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M12 11v5M9.5 13.5h5" />
    </>
  ),
  upload: (
    <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
  ),
  share: (
    <>
      <circle cx="18" cy="5.5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="18.5" r="2.5" />
      <path d="M8.2 10.8l7.6-4M8.2 13.2l7.6 4" />
    </>
  ),
  pointer: <path d="M5 3.5l6.5 16 2.3-6.7 6.7-2.3z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.8 2.6 4 5.6 4 9s-1.2 6.4-4 9c-2.8-2.6-4-5.6-4-9s1.2-6.4 4-9z" />
    </>
  ),
  pencil: <path d="M4 20l4-1L19.5 7.5a2.1 2.1 0 0 0-3-3L5 16zM14.5 6.5l3 3" />,
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4.5" width="14" height="16.5" rx="2" />
      <path d="M9 4.5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v.5M9 12h6M9 16h4" />
    </>
  ),
  link: (
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16v.3" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8v.3" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
  chevron: <path d="M6 9l6 6 6-6" />,
  send: <path d="M21 3L10 14M21 3l-7 18-3.5-7.5L3 10z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </>
  ),
};

const Icon = ({ name, size = 16 }) => (
  <svg
    className="ja-icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICONS[name]}
  </svg>
);

const DriveLogo = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 87.3 78"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

// ─── Field primitives ─────────────────────────────────────────────────────────
const Field = ({
  id,
  label,
  required,
  optional,
  hint,
  error,
  full,
  children,
}) => (
  <div
    className={`ja-field${error ? " ja-field--error" : ""}${full ? " ja-col-full" : ""}`}
  >
    <label className="ja-field__label" htmlFor={id}>
      {label}
      {required && (
        <span className="ja-field__req" aria-hidden="true">
          {" "}
          *
        </span>
      )}
      {optional && <span className="ja-field__opt">Optional</span>}
    </label>
    {children}
    {error ? (
      <span className="ja-field__error" id={`${id}-msg`} role="alert">
        <Icon name="alert" size={13} />
        {error}
      </span>
    ) : hint ? (
      <span className="ja-field__hint" id={`${id}-msg`}>
        {hint}
      </span>
    ) : null}
  </div>
);

// ─── Chip / status ────────────────────────────────────────────────────────────
const Chip = ({ icon, className = "", children }) => (
  <span className={`ja-meta-chip ${className}`}>
    <Icon name={icon} size={14} />
    {children}
  </span>
);

// ─── Job detail tabs ──────────────────────────────────────────────────────────
const TABS = ["Description", "Responsibilities", "Desired Profile"];
const JobDetailTabs = ({ job }) => {
  const [active, setActive] = useState(0);
  const content = [
    job.jobDescription,
    job.responsibilities,
    job.desiredProfile,
  ];
  return (
    <section className="ja-card ja-details" aria-label="Role details">
      <div className="ja-tabs__bar" role="tablist">
        {TABS.map((t, i) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={active === i}
            className={`ja-tabs__btn${active === i ? " ja-tabs__btn--active" : ""}`}
            onClick={() => setActive(i)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="ja-tabs__content" role="tabpanel">
        {content[active] ? (
          <p className="ja-tabs__text">{content[active]}</p>
        ) : (
          <p className="ja-tabs__empty">
            No {TABS[active].toLowerCase()} info provided.
          </p>
        )}
        {active === 0 && job.skills && (
          <div className="ja-skill-chips">
            {job.skills
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((s) => (
                <span key={s} className="ja-skill-chip">
                  {s}
                </span>
              ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ─── Full-details box (shown from the "i" icons) ──────────────────────────────
const HIDE_KEYS = new Set(["logo", "logoType", "logoName", "jobPostLink"]);
const BUDGET_KEYS = new Set(["budget", "budgetType", "salaryType"]);
const prettyKey = (k) =>
  k
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\bNo Of\b/i, "No. of");

const formatDetailValue = (k, v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    return v.every((x) => x === null || typeof x !== "object")
      ? v.filter(Boolean).join(", ")
      : null;
  }
  if (typeof v === "object") return null;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}(T|$)/.test(v))
    return fmtDate(v);
  if (typeof v === "number" && /budget|salary/i.test(k) && v > 0)
    return `₹${v.toLocaleString("en-IN")}`;
  return String(v);
};

const DetailsBox = ({ data }) => {
  const short = [];
  const long = [];
  Object.entries(data || {}).forEach(([k, v]) => {
    if (HIDE_KEYS.has(k) || /(^|[a-z])Id$|^id$/.test(k)) return;
    if (!data.showBudget && BUDGET_KEYS.has(k)) return;
    if (v === null || v === undefined || v === "") return;
    const val = formatDetailValue(k, v);
    if (!val) return;
    (val.length > 70 || val.includes("\n") ? long : short).push([
      prettyKey(k),
      val,
    ]);
  });
  if (!short.length && !long.length)
    return (
      <div className="ja-details-box">No additional details available.</div>
    );
  return (
    <div className="ja-details-box" role="region" aria-label="Full details">
      <dl className="ja-details-box__grid">
        {short.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {long.map(([k, v]) => (
        <div className="ja-details-box__long" key={k}>
          <h4>{k}</h4>
          <p>{v}</p>
        </div>
      ))}
    </div>
  );
};

const InfoToggle = ({ data, label = "View full details" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="ja-info-link"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="info" size={15} />
        {open ? "Hide details" : label}
      </button>
      {open && <DetailsBox data={data} />}
    </>
  );
};

// ─── Drive: role picker ───────────────────────────────────────────────────────
const DriveRoles = ({
  drive,
  selectedJobPostIds = [],
  onSelectJobPost,
  selectionError,
}) => {
  const [openInfo, setOpenInfo] = useState(null);
  const posts = (
    drive.jobPostSummaryDTOList ||
    drive.consultancyJobPostSummaryDTOList ||
    []
  ).map((p) => ({ ...p, jobPostId: p.jobPostId ?? p.consultancyJobPostId }));
  if (!posts.length) return null;

  return (
    <section
      className={`ja-card ja-roles${selectionError ? " ja-roles--error" : ""}`}
      aria-labelledby="ja-roles-title"
    >
      <div className="ja-roles__head">
        <h2 className="ja-card__title" id="ja-roles-title">
          Choose the roles you're applying for{" "}
          <span className="ja-field__req" aria-hidden="true">
            *
          </span>
        </h2>
        <span className="ja-roles__count">
          {selectedJobPostIds.length} selected
        </span>
      </div>
      {selectionError && (
        <div className="ja-inline-error" role="alert">
          <Icon name="alert" size={14} />
          {selectionError}
        </div>
      )}
      <div
        className="ja-roles__list"
        id="ja-selectedJobPostId"
        tabIndex={-1}
        role="group"
        aria-labelledby="ja-roles-title"
      >
        {posts.map((p) => {
          const isSelected = selectedJobPostIds.some(
            (id) => String(id) === String(p.jobPostId),
          );
          const isClosed = p.status?.toUpperCase() === "CLOSED";
          return (
            <div key={p.jobPostId} className="ja-role-wrap">
              <button
                type="button"
                disabled={isClosed}
                aria-pressed={isSelected}
                className={`ja-role${isSelected ? " ja-role--selected" : ""}${isClosed ? " ja-role--disabled" : ""}`}
                onClick={() => !isClosed && onSelectJobPost(p.jobPostId)}
              >
                <span className="ja-role__check">
                  {isSelected && <Icon name="check" size={13} />}
                </span>
                <span className="ja-role__info">
                  <span className="ja-role__title">
                    {p.jobTitle || p.role || `Post #${p.jobPostId}`}
                  </span>
                  <span className="ja-role__meta">
                    {p.department && <span>{p.department}</span>}
                    {p.role && p.role !== p.jobTitle && <span>{p.role}</span>}
                    {p.noOfVacancy && <span>{p.noOfVacancy} openings</span>}
                    {p.experience && <span>{p.experience} yrs exp.</span>}
                    {p.showBudget && p.budget > 0 && (
                      <span>
                        ₹{Number(p.budget).toLocaleString("en-IN")}
                        {p.budgetType ? ` / ${p.budgetType}` : ""}
                      </span>
                    )}
                  </span>
                  {p.description && (
                    <span className="ja-role__desc">
                      {p.description.slice(0, 100)}
                      {p.description.length > 100 ? "…" : ""}
                    </span>
                  )}
                </span>
                {p.status && (
                  <span
                    className={`ja-status-badge ja-status-badge--${p.status.toLowerCase()}`}
                  >
                    {p.status}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="ja-info-btn"
                aria-label={`Full details for ${p.jobTitle || p.role || "this role"}`}
                aria-expanded={openInfo === p.jobPostId}
                title="View full details"
                onClick={() =>
                  setOpenInfo((cur) =>
                    cur === p.jobPostId ? null : p.jobPostId,
                  )
                }
              >
                <Icon name="info" size={16} />
              </button>
              {openInfo === p.jobPostId && <DetailsBox data={p} />}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ─── Google Drive instructions (rendered ONCE, shared by Resume + Portfolio) ──
const DRIVE_STEPS = [
  {
    icon: "globe",
    text: (
      <>
        Open <strong>Google Drive</strong>.
      </>
    ),
  },
  {
    icon: "folderPlus",
    text: (
      <>
        Create a <strong>new folder</strong>.
      </>
    ),
  },
  {
    icon: "upload",
    text: (
      <>
        Upload both your <strong>Resume</strong> and <strong>Portfolio</strong>{" "}
        files into that folder.
      </>
    ),
  },
  {
    icon: "share",
    text: (
      <>
        Right-click the folder and select <strong>Share</strong>.
      </>
    ),
  },
  {
    icon: "pointer",
    text: (
      <>
        Click <strong>Share</strong> again if prompted.
      </>
    ),
  },
  {
    icon: "users",
    text: (
      <>
        Under <strong>General Access</strong>, change{" "}
        <strong>Restricted</strong> to <strong>Anyone with the link</strong>.
      </>
    ),
  },
  {
    icon: "pencil",
    text: (
      <>
        In the same access settings, change <strong>Viewer</strong> to{" "}
        <strong>Editor</strong>.
      </>
    ),
  },
  {
    icon: "copy",
    text: (
      <>
        Click <strong>Copy Link</strong>.
      </>
    ),
  },
  {
    icon: "clipboard",
    text: <>Paste the copied Google Drive folder link into the fields below.</>,
  },
];

const DriveGuide = () => {
  const [open, setOpen] = useState(true);
  return (
    <div className="ja-guide">
      <button
        type="button"
        className="ja-guide__toggle"
        aria-expanded={open}
        aria-controls="ja-guide-body"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ja-guide__logo">
          <DriveLogo />
        </span>
        <span className="ja-guide__heading">
          <span className="ja-guide__title">
            How to Upload Your Resume &amp; Portfolio Using Google Drive
          </span>
          <span className="ja-guide__desc">
            One shared folder link works for both fields below.
          </span>
        </span>
        <span
          className={`ja-guide__chev${open ? " ja-guide__chev--open" : ""}`}
        >
          <Icon name="chevron" size={18} />
        </span>
      </button>

      <div
        id="ja-guide-body"
        className={`ja-guide__body${open ? " ja-guide__body--open" : ""}`}
      >
        <div className="ja-guide__inner">
          <ol className="ja-guide__steps">
            {DRIVE_STEPS.map((s, i) => (
              <li key={i} className="ja-guide__step">
                <span className="ja-guide__step-icon">
                  <Icon name={s.icon} size={16} />
                  <span className="ja-guide__step-num">{i + 1}</span>
                </span>
                <span className="ja-guide__step-text">{s.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="ja-guide__important" role="note">
        <Icon name="alert" size={16} />
        <p>
          <strong>Important:</strong> Both your Resume and Portfolio must be
          uploaded inside the same Google Drive folder. Use the same copied
          folder link in both fields.
        </p>
      </div>
    </div>
  );
};

// ─── Link field card (Resume / Portfolio) ─────────────────────────────────────
const LinkField = ({
  icon,
  label,
  name,
  value,
  error,
  onChange,
  onBlur,
  placeholder,
  hint,
  action,
}) => {
  const id = `ja-${name}`;
  const kind = value && !error ? getDriveKind(value) : null;
  let status = null;
  if (error) {
    status = (
      <span
        className="ja-link-card__status ja-link-card__status--error"
        id={`${id}-msg`}
        role="alert"
      >
        <Icon name="alert" size={13} />
        {error}
      </span>
    );
  } else if (kind === "folder") {
    status = (
      <span
        className="ja-link-card__status ja-link-card__status--ok"
        id={`${id}-msg`}
      >
        <Icon name="check" size={13} />
        Google Drive folder link detected
      </span>
    );
  } else if (kind === "file") {
    status = (
      <span
        className="ja-link-card__status ja-link-card__status--warn"
        id={`${id}-msg`}
      >
        <Icon name="alert" size={13} />
        This looks like a single-file link. Share the folder that holds both
        documents.
      </span>
    );
  } else if (hint) {
    status = (
      <span className="ja-link-card__status" id={`${id}-msg`}>
        {hint}
      </span>
    );
  }

  return (
    <div className={`ja-link-card${error ? " ja-link-card--error" : ""}`}>
      <div className="ja-link-card__head">
        <span className="ja-link-card__icon">
          <Icon name={icon} size={18} />
        </span>
        <label className="ja-link-card__label" htmlFor={id}>
          {label}
        </label>
        <span className="ja-field__opt">Optional</span>
      </div>
      <div className="ja-input-wrap">
        <Icon name="link" size={15} />
        <input
          id={id}
          name={name}
          className={`ja-input ja-input--icon${error ? " ja-input--error" : ""}`}
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={status ? `${id}-msg` : undefined}
        />
      </div>
      {status}
      {action}
    </div>
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ icon, title, description, children }) => (
  <section className="ja-card ja-section">
    <header className="ja-section__intro">
      <span className="ja-section__icon">
        <Icon name={icon} size={18} />
      </span>
      <h3 className="ja-section__title">{title}</h3>
      <p className="ja-section__desc">{description}</p>
    </header>
    <div className="ja-section__body">{children}</div>
  </section>
);

// ─── Application form ─────────────────────────────────────────────────────────
const ApplicationForm = ({
  form,
  errors,
  onChange,
  onBlur,
  onQualTypeChange,
  onJoiningStatusChange,
  onCopyResumeToPortfolio,
  onSubmit,
  submitting,
  submitError,
  salaryLabel,
}) => {
  const bind = (name) => ({
    id: `ja-${name}`,
    name,
    value: form[name],
    onChange,
    onBlur,
    "aria-invalid": !!errors[name],
    "aria-describedby": errors[name] ? `ja-${name}-msg` : undefined,
  });
  const cls = (name) => `ja-input${errors[name] ? " ja-input--error" : ""}`;
  const noWheel = (e) => e.currentTarget.blur();

  const canCopy =
    isValidUrl(form.resumeUrl) &&
    normalizeUrl(form.resumeUrl) !== normalizeUrl(form.portfolioLink);

  return (
    <form className="ja-form" onSubmit={onSubmit} noValidate>
      <div className="ja-form__head">
        <h2 className="ja-form__title">Apply for this Position</h2>
        <p className="ja-form__sub">
          Fields marked as <span className="ja-form__star">*</span> are Required
          / Mandatory fields
        </p>
      </div>

      <Section
        icon="user"
        title="Personal Information"
        description="How we'll reach you about your application."
      >
        <div className="ja-grid">
          <Field
            id="ja-candidateName"
            label="Full Name"
            required
            error={errors.candidateName}
          >
            <input
              {...bind("candidateName")}
              className={cls("candidateName")}
              placeholder="Ravi Kumar"
              autoComplete="name"
            />
          </Field>
          <Field
            id="ja-email"
            label="Email Address"
            required
            error={errors.email}
          >
            <input
              {...bind("email")}
              className={cls("email")}
              type="email"
              placeholder="ravi@example.com"
              autoComplete="email"
            />
          </Field>
          <Field
            id="ja-contactNumber"
            label="Phone Number"
            required
            error={errors.contactNumber}
          >
            <input
              {...bind("contactNumber")}
              className={cls("contactNumber")}
              type="tel"
              placeholder="+91 98765 43210"
              autoComplete="tel"
            />
          </Field>
        </div>
      </Section>

      <Section
        icon="briefcase"
        title="Professional Information"
        description="Your education, experience and expected compensation."
      >
        <div className="ja-grid">
          <Field id="ja-qualificationType" label="Highest Qualification Type">
            <select
              {...bind("qualificationType")}
              className="ja-input"
              onChange={onQualTypeChange}
            >
              <option value="">Select type</option>
              {QUALIFICATION_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field id="ja-qualification" label="Highest Qualification">
            {form.qualificationType &&
            QUALIFICATION_OPTIONS[form.qualificationType] ? (
              <select {...bind("qualification")} className="ja-input">
                <option value="">Select qualification</option>
                {QUALIFICATION_OPTIONS[form.qualificationType].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                {...bind("qualification")}
                className="ja-input"
                placeholder="Select a type first"
                disabled={!form.qualificationType}
              />
            )}
          </Field>
          <Field id="ja-experience" label="Experience (years)">
            <input
              {...bind("experience")}
              className="ja-input"
              type="number"
              inputMode="decimal"
              min="0"
              onWheel={noWheel}
              placeholder="e.g. 3"
            />
          </Field>
          <Field id="ja-expectedSalary" label={salaryLabel}>
            <input
              {...bind("expectedSalary")}
              className="ja-input"
              type="number"
              inputMode="numeric"
              min="0"
              onWheel={noWheel}
              placeholder="e.g. 600000"
            />
          </Field>
          <Field
            id="ja-skills"
            label="Skills"
            hint="Separate skills with commas"
            full
          >
            <input
              {...bind("skills")}
              className="ja-input"
              placeholder="React, Node.js, AWS…"
            />
          </Field>
        </div>
      </Section>

      <Section
        icon="building"
        title="Current Employment"
        description="Where you are now and when you could start."
      >
        <div className="ja-grid">
          <Field id="ja-currentCompany" label="Current Company">
            <input
              {...bind("currentCompany")}
              className="ja-input"
              placeholder="e.g. Acme Corp"
              autoComplete="organization"
            />
          </Field>
          <Field id="ja-currentCompanyDesignation" label="Current Designation">
            <input
              {...bind("currentCompanyDesignation")}
              className="ja-input"
              placeholder="e.g. Senior Engineer"
              autoComplete="organization-title"
            />
          </Field>
          <Field id="ja-currentSalary" label="Current Salary (₹ Annual)">
            <input
              {...bind("currentSalary")}
              className="ja-input"
              type="number"
              inputMode="numeric"
              min="0"
              onWheel={noWheel}
              placeholder="e.g. 800000"
            />
          </Field>
          <Field id="ja-currentLocation" label="Current Location">
            <input
              {...bind("currentLocation")}
              className="ja-input"
              placeholder="e.g. Bengaluru"
            />
          </Field>
          <Field id="ja-preferredWorkLocation" label="Preferred Work Location">
            <input
              {...bind("preferredWorkLocation")}
              className="ja-input"
              placeholder="e.g. Bengaluru / Remote"
            />
          </Field>
          <Field id="ja-applicantJoiningStatus" label="Joining Status">
            <select
              {...bind("applicantJoiningStatus")}
              className="ja-input"
              onChange={onJoiningStatusChange}
            >
              <option value="">Select status</option>
              {JOINING_STATUS_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          {form.applicantJoiningStatus === "NOTICEPERIOD" && (
            <Field id="ja-noticePeriodDays" label="Notice Period (days)">
              <input
                {...bind("noticePeriodDays")}
                className="ja-input"
                type="number"
                inputMode="numeric"
                min="0"
                onWheel={noWheel}
                placeholder="e.g. 30"
              />
            </Field>
          )}
          <Field id="ja-expectedJoiningDate" label="Expected Joining Date">
            <input
              {...bind("expectedJoiningDate")}
              className="ja-input"
              type="date"
            />
          </Field>
        </div>
      </Section>

      <Section
        icon="file"
        title="Resume & Portfolio"
        description="Share both documents through a single Google Drive folder."
      >
        <div className="ja-docs">
          <DriveGuide />
          <div className="ja-docs__fields">
            <p className="ja-docs__lead">
              Paste the same folder link in both fields
            </p>
            <div className="ja-link-grid">
              <LinkField
                icon="file"
                label="Resume"
                name="resumeUrl"
                value={form.resumeUrl}
                error={errors.resumeUrl}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="https://drive.google.com/drive/folders/…"
                hint="Paste your Google Drive folder link"
              />
              <LinkField
                icon="gallery"
                label="Portfolio"
                name="portfolioLink"
                value={form.portfolioLink}
                error={errors.portfolioLink}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="https://drive.google.com/drive/folders/…"
                hint="Use the same folder link as your resume"
                action={
                  canCopy && (
                    <button
                      type="button"
                      className="ja-link-btn"
                      onClick={onCopyResumeToPortfolio}
                    >
                      <Icon name="copy" size={13} />
                      Use the same link as Resume
                    </button>
                  )
                }
              />
            </div>
          </div>
        </div>

        <div className="ja-grid ja-grid--after-docs">
          <Field
            id="ja-idProof"
            label="ID Proof Link"
            optional
            error={errors.idProof}
            hint="Aadhaar, PAN or Passport — a publicly accessible link"
            full
          >
            <div className="ja-input-wrap">
              <Icon name="idcard" size={15} />
              <input
                {...bind("idProof")}
                className={`${cls("idProof")} ja-input--icon`}
                type="text"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="https://drive.google.com/file/…"
              />
            </div>
          </Field>
        </div>
      </Section>

      <div className="ja-card ja-submit">
        {submitError && (
          <div className="ja-submit-error" role="alert">
            <Icon name="alert" size={16} />
            {submitError}
          </div>
        )}
        <div className="ja-submit__row">
          <p className="ja-privacy">
            <Icon name="lock" size={15} />
            <span>
              Your information is used solely for this recruitment process and
              will not be shared externally.
            </span>
          </p>
          <button
            type="submit"
            className="ja-submit-btn"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <span className="ja-btn-spinner" /> Submitting…
              </>
            ) : (
              <>
                Submit Application <Icon name="send" size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

// ─── State card (closed / not found / success) ────────────────────────────────
const StateCard = ({ icon, tone = "neutral", title, message, children }) => (
  <div className={`ja-card ja-state ja-state--${tone}`}>
    <span className="ja-state__icon">
      <Icon name={icon} size={28} />
    </span>
    <h2 className="ja-state__title">{title}</h2>
    {message && <p className="ja-state__msg">{message}</p>}
    {children}
  </div>
);

// ─── Top bar ──────────────────────────────────────────────────────────────────
const Topbar = ({ data, isDrive, isConsultancy }) => (
  <header className="ja-topbar">
    <div className="ja-topbar__inner">
      <div className="ja-topbar__logo">
        {data.logo ? (
          <img
            src={`data:${data.logoType};base64,${data.logo}`}
            alt={data.logoName || data.companyName}
            className="ja-topbar__company-logo"
          />
        ) : (
          <div className="ja-topbar__logo-fallback">
            {data.companyName?.charAt(0) ?? "C"}
          </div>
        )}
        <div className="ja-topbar__brand">
          <span className="ja-topbar__name">{data.companyName}</span>
          {data.branchName && (
            <span className="ja-topbar__branch">{data.branchName}</span>
          )}
        </div>
      </div>
      <span className="ja-topbar__code">
        {isDrive ? "Job Drive / Walk-in" : `Job #${data.jobPostCode}`}
        {isConsultancy && <span className="ja-topbar__badge">Consultancy</span>}
      </span>
    </div>
  </header>
);

// ─────────────────────────────────────────────────────────────────────────────
//  JobApplyPage
// ─────────────────────────────────────────────────────────────────────────────
const JobApplyPage = () => {
  const { tenantB64, jobPostId, jobDriveOrWalkinId, driveId } = useParams();
  const { pathname } = useLocation();

  const isConsultancy = pathname.startsWith("/consultancy/");
  const isDriveOnly = Boolean(driveId);
  const isDriveWithPost = Boolean(jobDriveOrWalkinId);
  const isDrive = isDriveOnly || isDriveWithPost;

  const resolvedDriveId = driveId || jobDriveOrWalkinId || null;
  const resolvedPostId = jobPostId || null;
  const tenantId = decodeTenantId(tenantB64);

  const [data, setData] = useState(null);
  const [dataLoading, setLoading] = useState(true);
  const [dataError, setError] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [appliedTitles, setAppliedTitles] = useState([]);
  const submittingRef = useRef(false); // blocks double-submits before state flushes

  useEffect(() => {
    if (!tenantId) {
      setError(true);
      setLoading(false);
      return;
    }

    let url;
    if (isConsultancy) {
      url = isDrive
        ? `${CONS_DRIVE_API}/${resolvedDriveId}`
        : `${CONS_POSTS_API}/${resolvedPostId}`;
    } else {
      url = isDrive
        ? `${DRIVE_API}/${resolvedDriveId}`
        : `${POSTS_API}/${resolvedPostId}`;
    }

    fetch(url, { headers: { "X-Tenantid": tenantId } })
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleQualificationTypeChange = (e) => {
    setForm((p) => ({
      ...p,
      qualificationType: e.target.value,
      qualification: "",
    }));
    if (errors.qualificationType)
      setErrors((p) => ({ ...p, qualificationType: "" }));
  };

  const handleJoiningStatusChange = (e) => {
    const value = e.target.value;
    setForm((p) => ({
      ...p,
      applicantJoiningStatus: value,
      noticePeriodDays: value === "NOTICEPERIOD" ? p.noticePeriodDays : "",
    }));
    if (errors.applicantJoiningStatus)
      setErrors((p) => ({ ...p, applicantJoiningStatus: "" }));
  };

  const handleSelectJobPost = (id) => {
    setForm((p) => {
      const selectedJobPostIds = p.selectedJobPostIds.includes(String(id))
        ? p.selectedJobPostIds.filter((postId) => postId !== String(id))
        : [...p.selectedJobPostIds, String(id)];
      return { ...p, selectedJobPostIds };
    });
    if (errors.selectedJobPostId)
      setErrors((p) => ({ ...p, selectedJobPostId: "" }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (
      !["candidateName", "email", "contactNumber", ...URL_FIELDS].includes(name)
    )
      return;
    let v = value;
    if (URL_FIELDS.includes(name) && value.trim()) {
      v = isValidUrl(value) ? normalizeUrl(value) : value.trim();
      if (v !== value) setForm((p) => ({ ...p, [name]: v }));
    }
    setErrors((p) => ({ ...p, [name]: validateField(name, v) }));
  };

  const handleCopyResumeToPortfolio = () => {
    setForm((p) => ({ ...p, portfolioLink: normalizeUrl(p.resumeUrl) }));
    setErrors((p) => ({ ...p, portfolioLink: "" }));
  };

  const validate = () => {
    const e = {};
    ["candidateName", "email", "contactNumber", ...URL_FIELDS].forEach((k) => {
      const msg = validateField(k, form[k]);
      if (msg) e[k] = msg;
    });

    if (isDrive && data) {
      const posts =
        data.jobPostSummaryDTOList ||
        data.consultancyJobPostSummaryDTOList ||
        [];
      if (posts.length > 0 && form.selectedJobPostIds.length === 0) {
        e.selectedJobPostId = "Please select at least one role to apply for";
      }
    }

    setErrors(e);
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setSubmitError("");
    const errs = validate();
    if (Object.keys(errs).length) {
      setSubmitError("Please review the highlighted fields and try again.");
      focusFirstError(errs);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);

    const candidateFields = {
      candidateName: form.candidateName.trim(),
      email: form.email.trim(),
      contactNumber: form.contactNumber.trim(),
      resumeUrl: urlOrUndef(form.resumeUrl),
      skills: form.skills || undefined,
      expectedSalary: form.expectedSalary
        ? Number(form.expectedSalary)
        : undefined,
      qualificationType: form.qualificationType || undefined,
      qualification: form.qualification || undefined,
      experience: form.experience ? Number(form.experience) : undefined,
      idProof: urlOrUndef(form.idProof),
      applicantJoiningStatus: form.applicantJoiningStatus || undefined,
      expectedJoiningDate: form.expectedJoiningDate
        ? new Date(form.expectedJoiningDate).toISOString()
        : undefined,
      noticePeriodDays: form.noticePeriodDays
        ? Number(form.noticePeriodDays)
        : undefined,
      currentLocation: form.currentLocation || undefined,
      currentCompany: form.currentCompany || undefined,
      // Backend has no dedicated "current salary" field, so append the
      // annual salary to currentCompanyDesignation as "Designation, Salary".
      currentCompanyDesignation:
        [
          form.currentCompanyDesignation || undefined,
          form.currentSalary ? `${form.currentSalary}` : undefined,
        ]
          .filter(Boolean)
          .join(", ") || undefined,
      preferredWorkLocation: form.preferredWorkLocation || undefined,
      portfolioLink: urlOrUndef(form.portfolioLink),
    };

    let url, payload;

    if (isConsultancy) {
      if (isDrive) {
        url = CONS_DRIVE_APPLY_API;
        payload = {
          ...candidateFields,
          consultancyJobPostId: form.selectedJobPostIds.map(Number),
          consultancyJobDriveOrWalkinId: Number(resolvedDriveId),
        };
      } else {
        url = CONS_APPLY_API;
        payload = {
          ...candidateFields,
          consultancyJobPostId: Number(resolvedPostId),
        };
      }
    } else {
      if (isDrive) {
        url = DRIVE_APPLY_API;
        payload = {
          ...candidateFields,
          jobPostId: form.selectedJobPostIds.map(Number),
          jobDriveOrWalkinId: Number(resolvedDriveId),
        };
      } else {
        url = APPLY_API;
        payload = { ...candidateFields, jobPostId: Number(resolvedPostId) };
      }
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Tenantid": tenantId },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.message || "Something went wrong.");
      }

      // Same shape for company + consultancy, job post + job drive:
      // data.jobPostApplicationStatus = [{ jobPostId, jobPostName, alreadyApplied }]
      const statuses = Array.isArray(result?.data?.jobPostApplicationStatus)
        ? result.data.jobPostApplicationStatus
        : [];

      if (statuses.length > 0) {
        const alreadyApplied = statuses.filter((s) => s.alreadyApplied);
        const newlyApplied = statuses.filter((s) => !s.alreadyApplied);

        // One toast per job post the candidate has already applied to
        alreadyApplied.forEach((s) =>
          toast.warn(
            `You have already applied to "${
              s.jobPostName || `Job Post #${s.jobPostId}`
            }".`,
            { toastId: `already-applied-${s.jobPostId}` },
          ),
        );

        // Nothing new was submitted → stay on the form, don't show success
        if (newlyApplied.length === 0) return;

        setAppliedTitles(
          newlyApplied.map((s) => s.jobPostName).filter(Boolean),
        );
      } else if (result?.data?.status === "ALREADY_APPLIED") {
        // Fallback: backend flagged ALREADY_APPLIED without a per-post list
        toast.warn(
          `You have already applied to "${data?.jobTitle || "this job"}".`,
          { toastId: "already-applied" },
        );
        return;
      }

      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (dataLoading)
    return (
      <div className="ja-page ja-page--center">
        <div className="ja-spinner" />
        <p className="ja-center-text">Loading details…</p>
      </div>
    );

  if (dataError || !data)
    return (
      <div className="ja-page ja-page--center">
        <StateCard
          icon="search"
          title="Listing not found"
          message="This listing may have expired or been removed."
        />
      </div>
    );

  // ── Success state ─────────────────────────────────────────────────────────
  if (submitted) {
    const appliedTitle = appliedTitles.length
      ? appliedTitles.join(", ")
      : isDrive
        ? (() => {
            const posts =
              data.jobPostSummaryDTOList ||
              data.consultancyJobPostSummaryDTOList ||
              [];
            const selectedIds = form.selectedJobPostIds;
            const selectedTitles = posts
              .filter((x) =>
                selectedIds.some(
                  (id) =>
                    String(x.jobPostId ?? x.consultancyJobPostId) ===
                    String(id),
                ),
              )
              .map((p) => p.jobTitle || p.role)
              .filter(Boolean);
            return selectedTitles.join(", ") || "this drive";
          })()
        : data.jobTitle;

    return (
      <div className="ja-page">
        <Topbar data={data} isDrive={isDrive} isConsultancy={isConsultancy} />
        <main className="ja-shell ja-shell--narrow">
          <StateCard
            icon="check"
            tone="success"
            title="Application submitted"
            message={
              <>
                Thank you, <strong>{form.candidateName}</strong>. Your
                application for <strong>{appliedTitle}</strong> has been
                received. We'll reach out to you at{" "}
                <strong>{form.email}</strong>.
              </>
            }
          >
            <ul className="ja-state__next">
              <li>Our recruitment team will review your application.</li>
              <li>
                If your profile is shortlisted, we'll contact you by email or
                phone.
              </li>
              <li>Please check your inbox (and spam folder) for updates.</li>
            </ul>
          </StateCard>
        </main>
      </div>
    );
  }

  // ── Drive: determine if the form should be shown ──────────────────────────
  // If drive has exactly 1 post and it's CLOSED → block form
  // If drive itself is not OPEN → block form
  // For a regular job post → existing OPEN check handles it
  const drivePosts = isDrive
    ? (
        data.jobPostSummaryDTOList ||
        data.consultancyJobPostSummaryDTOList ||
        []
      ).map((p) => ({ ...p, jobPostId: p.jobPostId ?? p.consultancyJobPostId }))
    : [];

  const driveFormBlocked =
    isDrive &&
    ((data.status && data.status.toUpperCase() !== "OPEN") ||
      drivePosts.length === 0 ||
      drivePosts.every((p) => p.status?.toUpperCase() === "CLOSED"));

  // If the drive has exactly one linked job post, treat that post's own
  // applicationClosingDate as the drive's expiry as well.
  const driveSinglePostExpired =
    isDrive &&
    drivePosts.length === 1 &&
    isPastDate(drivePosts[0]?.applicationClosingDate);

  // ── Application closing date / expiry (job posts, not drives) ─────────────
  // Falls back to expectedClosingTime for older payloads that don't yet
  // include applicationClosingDate.
  const closingDate = !isDrive
    ? data?.applicationClosingDate || data?.expectedClosingTime
    : null;
  const jobExpired = !isDrive && isPastDate(closingDate);

  const salaryLabel = (() => {
    if (isDrive) {
      const selectedPostId =
        form.selectedJobPostIds.length === 1
          ? form.selectedJobPostIds[0]
          : null;
      if (!selectedPostId) return "Expected Salary (₹)";
      const post = drivePosts.find(
        (p) => String(p.jobPostId) === String(selectedPostId),
      );
      return post
        ? buildSalaryLabel(post.salaryType, post.budgetType)
        : "Expected Salary (₹)";
    }
    return buildSalaryLabel(data?.salaryType, data?.budgetType);
  })();

  // ── Render ────────────────────────────────────────────────────────────────
  const driveFromTime = isDrive ? fmtTime(data.fromTime) : null;
  const driveToTime = isDrive ? fmtTime(data.toTime) : null;
  const statusKey = jobExpired ? "expired" : data.status?.toLowerCase();

  const formEl = (
    <ApplicationForm
      form={form}
      errors={errors}
      onChange={handleChange}
      onBlur={handleBlur}
      onQualTypeChange={handleQualificationTypeChange}
      onJoiningStatusChange={handleJoiningStatusChange}
      onCopyResumeToPortfolio={handleCopyResumeToPortfolio}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitError={submitError}
      salaryLabel={salaryLabel}
    />
  );

  return (
    <div className="ja-page">
      <Topbar data={data} isDrive={isDrive} isConsultancy={isConsultancy} />

      <main className="ja-shell ja-layout">
        <div className="ja-pagehead">
          <h1 className="ja-pagehead__title">Job Application Form</h1>
          <p className="ja-pagehead__sub">
            {isDrive ? "Job Drive / Walk-in" : data.jobTitle}
            {data.companyName ? ` at ${data.companyName}` : ""}
          </p>
        </div>
        <aside className="ja-side">
          {/* ── Header ── */}
          <section className="ja-card ja-hero">
            <div className="ja-hero__top">
              <h2 className="ja-hero__title">
                {isDrive ? "Job Drive / Walk-in" : data.jobTitle}
              </h2>
              {(isDrive ? data.status : data.status) && (
                <span
                  className={`ja-status-badge ja-status-badge--${statusKey}`}
                >
                  {jobExpired ? "EXPIRED" : data.status}
                </span>
              )}
            </div>

            <div className="ja-job-meta">
              {isDrive ? (
                <>
                  <Chip icon="calendar">
                    {fmtDate(data.fromDate)}
                    {data.toDate && data.toDate !== data.fromDate
                      ? ` – ${fmtDate(data.toDate)}`
                      : ""}
                  </Chip>
                  {(driveFromTime || driveToTime) && (
                    <Chip icon="clock">
                      {driveFromTime || ""}
                      {driveFromTime && driveToTime ? " – " : ""}
                      {driveToTime || ""}
                    </Chip>
                  )}
                  {data.venue && <Chip icon="pin">{data.venue}</Chip>}
                  {data.mode && (
                    <Chip icon={data.mode === "ONLINE" ? "globe" : "briefcase"}>
                      {data.mode}
                    </Chip>
                  )}
                </>
              ) : (
                <>
                  {data.department && (
                    <Chip icon="building">{data.department}</Chip>
                  )}
                  {data.roleName && <Chip icon="target">{data.roleName}</Chip>}
                  {data.jobType && <Chip icon="briefcase">{data.jobType}</Chip>}
                  {data.employmentType && (
                    <Chip icon="clock">{data.employmentType}</Chip>
                  )}
                  {(data.city || data.state) && (
                    <Chip icon="pin">
                      {[data.city, data.state, data.country]
                        .filter(Boolean)
                        .join(", ")}
                    </Chip>
                  )}
                  {data.experience && (
                    <Chip icon="user">{data.experience} yrs</Chip>
                  )}
                  {data.noOfVacancy && (
                    <Chip icon="users">
                      {data.noOfVacancy} opening
                      {data.noOfVacancy !== 1 ? "s" : ""}
                    </Chip>
                  )}
                  {data.showBudget && data.budget > 0 && (
                    <Chip icon="rupee">
                      {Number(data.budget).toLocaleString()}
                    </Chip>
                  )}
                  {closingDate && (
                    <Chip
                      icon="calendar"
                      className={
                        jobExpired
                          ? "ja-meta-chip--expired"
                          : "ja-meta-chip--closing"
                      }
                    >
                      {jobExpired ? "Expired" : "Closes"} {fmtDate(closingDate)}
                    </Chip>
                  )}
                </>
              )}
            </div>
            <InfoToggle data={data} />
          </section>

          {isDrive ? (
            <DriveRoles
              drive={data}
              selectedJobPostIds={form.selectedJobPostIds}
              onSelectJobPost={handleSelectJobPost}
              selectionError={errors.selectedJobPostId}
            />
          ) : (
            <JobDetailTabs job={data} />
          )}
        </aside>
        <div className="ja-main">
          {/* ── Form / closed guard ── */}
          {isDrive ? (
            drivePosts.length === 0 ? (
              <StateCard
                icon="clock"
                title="No Job Posts Available"
                message="There are currently no job posts mapped to this Job Drive / Walk-In."
              />
            ) : driveSinglePostExpired ? (
              <StateCard
                icon="clock"
                title="Applications Closed"
                message={`Applications for this job closed on ${fmtDate(
                  drivePosts[0]?.applicationClosingDate,
                )}.`}
              />
            ) : driveFormBlocked ? (
              <StateCard
                icon="clock"
                title="Job Post Closed"
                message="This job posting is no longer accepting applications."
              />
            ) : (
              formEl
            )
          ) : data.status?.toUpperCase() === "OPEN" && !jobExpired ? (
            formEl
          ) : jobExpired ? (
            <StateCard
              icon="clock"
              title="Job Expired"
              message={`Applications for this job closed on ${fmtDate(closingDate)}.`}
            />
          ) : (
            <StateCard
              icon="clock"
              title="Job Post Closed"
              message="This job posting is no longer accepting applications."
            />
          )}
        </div>
      </main>

      <footer className="ja-page-footer">
        {isDrive ? (
          <>
            <span>{fmtDate(data.fromDate)}</span>
            {data.toDate && data.toDate !== data.fromDate && (
              <span>Ends {fmtDate(data.toDate)}</span>
            )}
            {data.venue && <span>{data.venue}</span>}
          </>
        ) : (
          closingDate && (
            <span>
              {jobExpired ? "Expired" : "Closes"} {fmtDate(closingDate)}
            </span>
          )
        )}
      </footer>
    </div>
  );
};

export default JobApplyPage;
